/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';

import { useViewType, ViewType } from '@eika/hooks';

import { useConfig } from '../../../defaultConfig';
import { useAccountFreeWithdrawals, useAccounts } from '../../../fetchHooks/useAccount';
import type { Account, AccountNumber } from '../../../generated-types';
import { useTransferAccountsContext } from '../../../hooks/useAccountState';
import { isValidAccountTransferFrom } from '../../../services/accountFilters';
import filterInvalidAccounts from '../../../utils/filterInvalidAccounts';
import { DownpaymentDesktopFields, DownpaymentMobileFields } from './fields';
import FieldProps from './fields/FieldProps';
import useFormContext from './form/useFormContext';

interface Props {
    locked?: boolean;
    shouldFocusFirstElement?: boolean;
    isFuturePaymentsDetailsTransfer?: boolean;
    loanAccount?: Account;
    isLoading?: boolean;
    selectLoanAccount: { mutate: (item: AccountNumber | undefined) => void; isLoading: boolean };
    isFixedLoan: boolean;
}

const DownpaymentFields: React.FC<Props> = ({
    locked = false,
    shouldFocusFirstElement = false,
    loanAccount,
    isLoading = false,
    selectLoanAccount,
    isFixedLoan,
}) => {
    const { featureToggles } = useConfig();
    const form = useFormContext();

    const { data: allAccounts, ...accountState } = useAccounts();
    const validFromAccounts = allAccounts?.filter(isValidAccountTransferFrom);
    const invalidAccounts = filterInvalidAccounts({
        allAccounts,
        validAccounts: validFromAccounts,
    })?.filter((ac) => ac.accountNumber.value !== loanAccount?.accountNumber.value);

    const { targetFromAccount } = useTransferAccountsContext();
    const { data: freeWithdrawals, showFreewithdrawal } = useAccountFreeWithdrawals(
        targetFromAccount,
        featureToggles.enableGettingAccountWithdrawalsInPayment,
    );

    const viewType = useViewType();
    const isMobile = viewType === ViewType.mobile;

    const fieldProps: FieldProps = {
        form,
        locked,
        shouldFocusFirstElement,
        freeWithdrawals: {
            data: freeWithdrawals,
            show: showFreewithdrawal,
        },
        loanAccount,
        allAccounts,
        validFromAccounts,
        invalidAccounts,
        accountState: {
            ...accountState,
            isLoading: accountState.isLoading || isLoading || selectLoanAccount.isLoading,
        },
        selectLoanAccount: selectLoanAccount.mutate,
        isFixedLoan,
    };

    return isMobile ? <DownpaymentMobileFields {...fieldProps} /> : <DownpaymentDesktopFields {...fieldProps} />;
};

export default DownpaymentFields;
